import { Button } from '@calizahq/react-components-v2'
import React, { useEffect, useMemo, useState } from 'react'

import { Checkbox, Dropdown } from '@/components'
import { useUserContext } from '@/context'
import { useTicketFiltersContext } from '@/context/filters/ticketFilters/ticketFilters'
import { TicketTypesQueries } from '@/graphql/queries/ticketTypes.queries'
import { useMobile } from '@/hooks'
import { useGraphql } from '@/hooks/useGraphql'
import { TicketType } from '@/types/TicketType'

const CONVERSATION_TYPE = 'Conversation'

export const TypeFilter = () => {
  const { setFilters, ticketTypes } = useTicketFiltersContext()
  const { user } = useUserContext()
  const isSuperAdmin = !!user?.isSuperAdmin
  const [dropdownExpanded, setDropdownExpanded] = useState(false)
  const [selectedTypes, setSelectedTypes] = useState<string[]>(ticketTypes || [])
  const { isMobile } = useMobile()
  const { data: typesData, isLoading } = useGraphql<{ ticketTypes: TicketType[] }>(
    TicketTypesQueries.QUERY_KEY,
    TicketTypesQueries.getTicketTypes(),
  )

  // Sync local state when context changes
  useEffect(() => {
    setSelectedTypes(ticketTypes || [])
  }, [ticketTypes])

  const ticketTypeOptions = useMemo(() => {
    return !isLoading && typesData?.ticketTypes
      ? typesData?.ticketTypes
          .map((t) => ({ val: t.description, label: t.description }))
          .filter((t) => isSuperAdmin || t.val !== CONVERSATION_TYPE)
          .sort((a, b) => a.label.localeCompare(b.label))
      : []
  }, [typesData?.ticketTypes, isLoading, isSuperAdmin])

  const handleClick = (val: string) => {
    if (selectedTypes.includes(val)) {
      setSelectedTypes(selectedTypes.filter((t) => t !== val))
    } else {
      setSelectedTypes([...selectedTypes, val])
    }
  }

  const handleApply = () => {
    setFilters({
      ticketTypes: selectedTypes.length > 0 ? selectedTypes : null,
      ticketReasons: null,
    })
    setDropdownExpanded(false)
  }

  const handleReset = () => {
    setSelectedTypes([])
    setFilters({ ticketTypes: null, ticketReasons: null })
    setDropdownExpanded(false)
  }

  const handleCancel = () => {
    setSelectedTypes(ticketTypes || [])
    setDropdownExpanded(false)
  }

  const renderButtons = () => {
    return (
      <div className="flex flex-row items-center justify-between px-6 pb-4 pt-4">
        {isMobile ? (
          <Button
            className="mr-4 flex-1 bg-grey-100"
            onClick={handleReset}
            size={isMobile ? undefined : 'small'}
            variant={'secondary'}
          >
            Reset
          </Button>
        ) : (
          <Button onClick={handleReset} size={isMobile ? undefined : 'small'} variant={'secondary'}>
            <span className="underline underline-offset-2">Reset</span>
          </Button>
        )}

        <Button
          className="flex-1 bg-text-semantic-success md:flex-0"
          onClick={handleApply}
          size={isMobile ? undefined : 'small'}
        >
          Apply
        </Button>
      </div>
    )
  }

  return (
    <Dropdown
      className="hidden w-full md:w-auto lg:flex"
      displayedCount={ticketTypes?.length ?? 0}
      expanded={dropdownExpanded}
      headerTitle="Type"
      onHideDropdown={handleCancel}
      renderFooter={renderButtons}
      selected={!!ticketTypes?.length}
      setExpanded={setDropdownExpanded}
    >
      <div className="md:w-80">
        <div className="grid max-h-72 grid-cols-1 gap-y-4 px-6 py-4 md:overflow-auto">
          {ticketTypeOptions.map(({ val, label }) => (
            <div
              className="flex cursor-pointer flex-row items-center"
              key={val}
              onClick={() => handleClick(val)}
              role="presentation"
            >
              <Checkbox checked={selectedTypes.includes(val)} onClick={() => handleClick(val)} />
              <div className="text-sm">{label}</div>
            </div>
          ))}
        </div>
      </div>
    </Dropdown>
  )
}
